import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import Header from "../components/Header";
import Footer from "../components/Footer";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  FileText, 
  Calculator, 
  ChevronRight,
  CheckCircle,
  Lock,
  CreditCard,
  DollarSign,
  Phone,
  ShieldCheck,
  FileCheck,
  Headphones,
  CalendarCheck,
  Briefcase,
  Home,
  User,
  Medal,
  Upload,
  ClipboardCheck,
  UserCheck,
  Globe,
  Languages,
  Star,
  Quote,
  TrendingUp,
  Plus,
  X
} from "lucide-react";
import { motion } from "framer-motion";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Images
const OWNER_HEADSHOT = "/images/owner-hero.jpg";
const OWNER_BRANDED = "/images/owner-about.jpg";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_taxfaster/artifacts/itpqphd5_1F0355F1-D4E9-4607-86B6-6AF60B9FC8A3.png";
const LOGO_FULL = "https://customer-assets.emergentagent.com/job_taxservice-2/artifacts/e9oq2ns0_IMG_7013.png";

// Services - Tax first, then Credit Education
const services = [
  {
    icon: FileText,
    title: "Individual Tax",
    description: "Tax preparation for individuals and families."
  },
  {
    icon: User,
    title: "Self-Employed",
    description: "Services for freelancers and contractors."
  },
  {
    icon: Calculator,
    title: "Business Tax",
    description: "Solutions for small businesses and LLCs."
  },
  {
    icon: CreditCard,
    title: "Credit Repair",
    description: "Improve your credit score and financial health."
  },
  {
    icon: DollarSign,
    title: "Funding",
    description: "Business funding and financing solutions."
  }
];

const whoWeServe = [
  { icon: Home, label: "Individuals & Families" },
  { icon: Briefcase, label: "Small Businesses & LLCs" },
  { icon: User, label: "Self-Employed & 1099 Workers" },
  { icon: Medal, label: "Veterans & Working Professionals" }
];

const trustBadges = [
  { icon: ShieldCheck, label: "IRS-Compliant & Secure" },
  { icon: Medal, label: "Veteran-Owned" },
  { icon: Globe, label: "Nationwide Support" },
  { icon: Lock, label: "Secure Uploads" }
];

// How It Works - 4 steps for Phase 4
const howItWorks = [
  { step: "1", icon: ClipboardCheck, title: "Submit Your Intake", description: "Secure online form with your personal and tax information." },
  { step: "2", icon: UserCheck, title: "We Prepare Your Return", description: "Reviewed by a professional tax preparer." },
  { step: "3", icon: FileCheck, title: "We File Electronically", description: "Fast & IRS-compliant e-filing." },
  { step: "4", icon: CheckCircle, title: "Return Completed", description: "Refund tracking + ongoing support." }
];

const whyChooseUs = [
  "IRS-Compliant Filing Process",
  "Secure Client Portal & Document Upload",
  "Personalized One-on-One Service",
  "Year-Round Support (Not Seasonal Only)",
  "Transparent Pricing — No Hidden Fees",
  "Nationwide Service — All 50 States"
];

const features = [
  "Experienced Tax Professionals",
  "Personalized Service",
  "Year-Round Support",
  "Secure Document Handling",
  "Competitive Pricing",
  "Fast Turnaround"
];

// Client Reviews/Testimonials - Default/fallback data
const defaultReviews = [
  {
    name: "Marcus T.",
    location: "Memphis, TN",
    rating: 5,
    review: "WMJ Enterprise Services made my tax filing so easy! As a small business owner, I was stressed about getting everything right. They handled everything professionally and got me a bigger refund than expected.",
    service: "Business Tax Preparation"
  },
  {
    name: "Keisha W.",
    location: "Atlanta, GA",
    rating: 5,
    review: "Finally found a tax preparer I can trust! The team is responsive, knowledgeable, and truly cares about their clients. They explained everything clearly and helped me understand my taxes better.",
    service: "Individual Tax Preparation"
  },
  {
    name: "David M.",
    location: "Houston, TX",
    rating: 5,
    review: "As a 1099 contractor, taxes used to stress me out every year. WMJ took that stress away completely. Professional service, great communication, and they found deductions I didn't even know about!",
    service: "Self-Employed Tax Services"
  },
  {
    name: "Shanice R.",
    location: "Charlotte, NC",
    rating: 5,
    review: "The credit education coaching was exactly what I needed. They didn't just file my taxes - they helped me understand my finances better. Highly recommend their services!",
    service: "Credit Education & Tax Prep"
  },
  {
    name: "Anthony J.",
    location: "Nashville, TN",
    rating: 5,
    review: "Military family here - WMJ understands our unique situation. Quick turnaround, fair pricing, and they were available to answer all my questions. Will definitely be back next year!",
    service: "Individual Tax Preparation"
  },
  {
    name: "Tamara L.",
    location: "Dallas, TX",
    rating: 5,
    review: "I've been using WMJ for two years now. The consistency and professionalism keep me coming back. They treat you like family, not just another client.",
    service: "Individual Tax Preparation"
  }
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [clientReviews, setClientReviews] = useState(defaultReviews);
  const [reviewsLoaded, setReviewsLoaded] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    name: "",
    location: "",
    rating: 5,
    review: "",
    service: "Tax Preparation"
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  // Booking state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookingForm, setBookingForm] = useState({ name: "", email: "", phone: "" });
  const [isBooking, setIsBooking] = useState(false);
  const [loadingTimes, setLoadingTimes] = useState(false);

  // Show only 3 reviews unless "View All" is clicked
  const displayedReviews = showAllReviews ? clientReviews : clientReviews.slice(0, 3);

  // Auto-collapse reviews after 1 minute of inactivity
  useEffect(() => {
    let timer;
    if (showAllReviews) {
      timer = setTimeout(() => {
        setShowAllReviews(false);
      }, 60000); // 60 seconds = 1 minute
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showAllReviews]);

  useEffect(() => {
    // Fetch reviews from API
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/reviews`);
        if (response.data && response.data.length > 0) {
          setClientReviews(response.data);
        }
        // If no reviews in DB, keep default reviews
      } catch (error) {
        console.log("Using default reviews");
        // Keep default reviews on error
      } finally {
        setReviewsLoaded(true);
      }
    };
    fetchReviews();
  }, []);

  // Fetch available times when date is selected
  const handleDateSelect = async (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setLoadingTimes(true);
    
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const response = await axios.get(`${API_URL}/api/appointments/available-times/${dateStr}`);
      setAvailableTimes(response.data || []);
    } catch (error) {
      console.error("Error fetching times:", error);
      setAvailableTimes([]);
      toast.error("Unable to load available times");
    } finally {
      setLoadingTimes(false);
    }
  };

  // Book appointment
  const handleBookAppointment = async () => {
    if (!selectedDate || !selectedTime || !bookingForm.name || !bookingForm.email) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setIsBooking(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      await axios.post(`${API_URL}/api/appointments/book`, {
        date: dateStr,
        time: selectedTime,
        client_name: bookingForm.name,
        client_email: bookingForm.email,
        client_phone: bookingForm.phone
      });
      
      toast.success("Appointment booked successfully! Check your email for confirmation.");
      setShowBookingModal(false);
      setSelectedDate(null);
      setSelectedTime(null);
      setBookingForm({ name: "", email: "", phone: "" });
      
      // Redirect to intake form
      setTimeout(() => {
        navigate("/intake");
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to book appointment");
    } finally {
      setIsBooking(false);
    }
  };

  // Disable past dates and Sundays
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day = date.getDay();
    return date < today || day === 0; // Disable past dates and Sundays
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.name || !reviewForm.review) {
      return;
    }
    setSubmittingReview(true);
    try {
      await axios.post(`${API_URL}/api/reviews/submit`, reviewForm);
      setShowBookingModal(false);
      setReviewForm({ name: "", location: "", rating: 5, review: "", service: "Tax Preparation" });
      // Show thank you message
      toast.success("Thank you for your review! It will be visible after approval.");
      setShowReviewModal(false);
    } catch (error) {
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="landing-page">
      <Header />

      {/* Main content wrapper with proper spacing for fixed header */}
      <main className="pt-[64px] md:pt-[94px]">
        {/* Tax Season Banner - positioned below fixed header */}
        <div 
          className="bg-[#C9A227] text-[#121417] py-3 text-center"
          data-testid="tax-banner"
        >
          <p className="text-sm md:text-base font-semibold px-4">
            📢 Tax Season Is Open — Limited availability. Secure your filing spot today.
          </p>
        </div>

        {/* Hero Section */}
        <section 
          className="relative min-h-[70vh] flex items-center bg-[#F7F5F0]"
          data-testid="hero-section"
        >
        <div className="absolute inset-0 bg-gradient-to-b from-[#F7F5F0] via-[#F3EFE6] to-[#EFECE6]"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <motion.div 
              className="lg:col-span-7 text-center lg:text-left"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Veteran Badge */}
              <div className="flex justify-center lg:justify-start mb-4">
                <span className="inline-flex items-center gap-2 bg-[#121417] text-gold px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider shadow-md" data-testid="veteran-badge">
                  <Medal size={16} />
                  Veteran-Owned &amp; Operated
                </span>
              </div>
              {/* Languages & Nationwide Badge */}
              <div className="flex flex-wrap gap-3 mb-6 justify-center lg:justify-start">
                <span className="inline-flex items-center gap-2 bg-white text-[#121417] border border-[#E2DDD3] px-3 py-1.5 rounded-md text-sm shadow-sm">
                  <Globe size={14} className="text-gold" />
                  Nationwide — All 50 States
                </span>
                <span className="inline-flex items-center gap-2 bg-white text-[#121417] border border-[#E2DDD3] px-3 py-1.5 rounded-md text-sm shadow-sm">
                  <Languages size={14} className="text-gold" />
                  English • French • Creole
                </span>
              </div>
              
              {/* Phase 4: Conversion-Focused Headline */}
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-[#121417] leading-tight mb-6">
                Professional Tax Filing.<br />
                <span className="text-gold">Simple. Secure. Reliable.</span>
              </h1>
              <p className="text-lg md:text-xl text-[#4A4E54] mb-8 max-w-xl leading-relaxed mx-auto lg:mx-0">
                File your taxes with confidence. WMJ Enterprise Services provides accurate, compliant tax preparation designed to make the process smooth and stress-free.
              </p>
              
              {/* Primary & Secondary CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8 justify-center lg:justify-start">
                <Link to="/intake" data-testid="hero-cta-primary">
                  <Button className="bg-[#121417] text-gold hover:bg-[#26292E] font-bold px-10 py-5 rounded-md text-lg transition-all duration-300 shadow-lg">
                    Start Your Tax Filing
                    <ChevronRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="border-2 border-[#121417] text-[#121417] bg-transparent hover:bg-[#121417] hover:text-white font-semibold px-8 py-5 rounded-md text-lg transition-colors"
                  onClick={() => setShowBookingModal(true)}
                  data-testid="hero-cta-schedule"
                >
                  <CalendarCheck className="mr-2" size={20} />
                  Schedule Appointment
                </Button>
              </div>
              
              {/* Trust Strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 justify-items-center lg:justify-items-start">
                {trustBadges.map((badge, index) => (
                  <div key={index} className="flex items-center gap-2 text-[#121417] text-sm font-medium">
                    <CheckCircle size={16} className="text-gold flex-shrink-0" />
                    <span>{badge.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Hero Image with Ground Shadow - Shows on all screens */}
            <motion.div 
              className="lg:col-span-5 flex justify-center order-first lg:order-last"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative">
                <img 
                  src={OWNER_HEADSHOT}
                  alt="WMJ Enterprise Services Professional"
                  className="w-full max-w-xs sm:max-w-sm lg:max-w-md mx-auto rounded-2xl shadow-2xl border-4 border-white/10 object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-10 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-navy">
              Who We Serve
            </h2>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {whoWeServe.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex flex-col items-center text-center p-6 rounded-lg bg-[#F4F1E9] hover:bg-gold/10 transition-colors duration-300"
              >
                <div className="w-12 h-12 bg-navy/10 rounded-full flex items-center justify-center mb-4">
                  <item.icon className="text-navy" size={24} />
                </div>
                <span className="font-medium text-navy">{item.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section - 4 Steps */}
      <section className="py-12 bg-[#F4F1E9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">Simple Process</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-navy mt-3">
              How It Works
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="relative inline-block mb-4">
                  <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center mx-auto">
                    <item.icon className="text-gold" size={24} />
                  </div>
                  <span className="absolute -top-1 -right-1 w-6 h-6 bg-gold text-navy rounded-full flex items-center justify-center font-bold text-sm">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-heading font-semibold text-base text-navy mb-1">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-12 md:py-16 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">What We Offer</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-navy mt-3 mb-4">
              Our Services
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              Professional tax preparation and financial services tailored to your needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="service-card h-full bg-white border border-slate-200 shadow-card hover:border-gold/30 rounded-lg" data-testid={`service-card-${index}`}>
                  <CardContent className="p-4">
                    <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center mb-3">
                      <service.icon className="text-navy" size={20} />
                    </div>
                    <h3 className="font-heading font-semibold text-sm text-navy mb-2">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-xs">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Clients Choose WMJ Section */}
      <section className="py-10 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white">
              Why Clients Choose WMJ for Tax Preparation
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-center gap-3 bg-white/10 rounded-lg p-4"
              >
                <CheckCircle className="text-gold flex-shrink-0" size={20} />
                <span className="text-white font-medium">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section with Owner Photo & Bio */}
      <section id="about" className="py-12 md:py-16 bg-[#F4F1E9]" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <motion.div 
              className="lg:col-span-5"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src={OWNER_BRANDED}
                alt="WMJ Enterprise Services Professional"
                className="rounded-lg shadow-2xl w-full object-cover"
              />
            </motion.div>
            <motion.div 
              className="lg:col-span-7 text-center lg:text-left"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">About Us</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-navy mt-3 mb-6">
                Your Trusted Tax Partner
              </h2>
              
              {/* Bio - Shortened */}
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                I am a <strong>U.S. military veteran</strong>, husband, and father committed 
                to discipline, integrity, and service. With a background forged through military service, 
                I bring <strong>attention to detail</strong> and <strong>reliability</strong> to helping 
                individuals and small businesses navigate their tax needs with confidence.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                My mission is to deliver <strong>honest, client-focused</strong> tax services while building 
                <strong> long-term relationships</strong> based on trust and professionalism.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 justify-items-center lg:justify-items-start">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="text-gold flex-shrink-0" size={20} />
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
              <Link to="/intake" data-testid="about-cta">
                <Button className="bg-navy text-white hover:bg-navy-dark font-semibold px-8 py-3 rounded-md transition-all duration-300">
                  Start Your Tax Filing
                  <ChevronRight className="ml-2" size={18} />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Credit Consultant Section */}
      <section id="credit-consultant" className="py-16 md:py-20 bg-white border-y border-[#E2DDD3]" data-testid="credit-consultant-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <motion.div 
              className="lg:col-span-7 text-center lg:text-left order-last lg:order-first"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-gold font-bold text-xs uppercase tracking-[0.2em]">Dedicated Credit Consulting &amp; Restoration</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-[#121417] mt-3 mb-6">
                Master Your Credit Score.<br />
                <span className="text-gold">Unlock Wealth &amp; Funding.</span>
              </h2>
              <p className="text-[#4A4E54] text-lg leading-relaxed mb-8">
                Custom 1-on-1 credit restoration strategies, hard inquiry disputes, FCRA compliance audits, 
                and strategic score elevation for VA loans, mortgages, and business expansion.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {[
                  { icon: ShieldCheck, label: "Bureau Audit & Dispute Strategy (Equifax, Experian, TransUnion)" },
                  { icon: Home, label: "VA Loan & Mortgage Credit Elevation" },
                  { icon: Briefcase, label: "Business Credit Building (PG & Non-PG lines)" },
                  { icon: TrendingUp, label: "Debt-to-Income Optimization & Collector Defense" }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-[#F7F5F0] border border-[#E2DDD3] rounded-lg p-4 text-left">
                    <item.icon className="text-gold flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-[#121417] text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
              <Link to="/credit-repair" data-testid="credit-consultant-assessment-button">
                <Button className="bg-gold text-[#121417] hover:bg-gold-hover font-bold px-8 py-4 rounded-md text-lg transition-all duration-300 shadow-md">
                  Request Free Credit Assessment
                  <ChevronRight className="ml-2" size={20} />
                </Button>
              </Link>
            </motion.div>
            <motion.div 
              className="lg:col-span-5"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src="/images/owner-story.jpg"
                alt="WMJ Credit Consultant"
                className="rounded-2xl border-2 border-gold/40 shadow-2xl w-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Second Photo Section - Why Choose Us */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div 
              className="lg:col-span-7 text-center lg:text-left"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">Why Choose Us</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-navy mt-3 mb-6">
                Professional Service You Can Count On
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                We take pride in delivering exceptional service to every client. Our commitment to 
                excellence has made us a trusted name in tax preparation and financial consulting.
              </p>
              <ul className="space-y-4 mb-8 inline-block text-left">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gold flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-700"><strong>Accurate Tax Preparation</strong> — Maximize your refund with precise, thorough filing</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gold flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-700"><strong>Client-Focused Credit Education</strong> — Understand and improve your financial standing</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gold flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-700"><strong>Strategic Business Consulting</strong> — Get guidance to help grow your business</span>
                </li>
              </ul>
              <div>
                <a href="tel:+19014013054">
                  <Button className="bg-gold text-navy hover:bg-gold-hover font-semibold px-8 py-3 rounded-md transition-all duration-300">
                    <Phone className="mr-2" size={18} />
                    Call Now: (901) 401-3054
                  </Button>
                </a>
              </div>
            </motion.div>
            <motion.div 
              className="lg:col-span-5"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {/* Full Logo on Right Side */}
              <div className="flex justify-center items-center p-8">
                <img 
                  src={LOGO_FULL}
                  alt="WMJ Enterprise Services - Tax & Financial Solutions"
                  className="w-full max-w-md mx-auto"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Client Reviews Section */}
      <section className="py-12 md:py-16 bg-[#F4F1E9]" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">Testimonials</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-navy mt-3 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              Don't just take our word for it — hear from clients who trust WMJ Enterprise Services with their taxes.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedReviews.map((review, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`review-card-${index}`}
              >
                <Card className="h-full bg-white border border-slate-200 shadow-card hover:shadow-lg hover:border-gold/30 transition-all duration-300 rounded-lg overflow-hidden">
                  <CardContent className="p-6 flex flex-col h-full">
                    {/* Quote Icon */}
                    <div className="mb-4">
                      <Quote className="text-gold/30" size={32} />
                    </div>
                    
                    {/* Review Text */}
                    <p className="text-slate-600 leading-relaxed flex-grow mb-6 italic">
                      "{review.review}"
                    </p>
                    
                    {/* Star Rating */}
                    <div className="flex gap-1 mb-4">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="text-gold fill-gold" size={18} />
                      ))}
                    </div>
                    
                    {/* Client Info */}
                    <div className="border-t border-slate-100 pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-navy">{review.name}</p>
                          <p className="text-slate-500 text-sm">{review.location}</p>
                        </div>
                        <span className="text-xs bg-navy/10 text-navy px-2 py-1 rounded-full">
                          {review.service}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* View All Reviews Button - only show if more than 3 reviews */}
          {clientReviews.length > 3 && !showAllReviews && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-8 text-center"
            >
              <Button
                onClick={() => setShowAllReviews(true)}
                variant="outline"
                className="border-navy text-navy hover:bg-navy hover:text-white font-semibold px-8 py-3 rounded-lg"
                data-testid="view-all-reviews-btn"
              >
                View All Reviews ({clientReviews.length})
              </Button>
            </motion.div>
          )}

          {/* Show Less Button when all reviews are shown */}
          {showAllReviews && clientReviews.length > 3 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-8 text-center"
            >
              <Button
                onClick={() => setShowAllReviews(false)}
                variant="outline"
                className="border-slate-300 text-slate-600 hover:bg-slate-100 font-medium px-6 py-2 rounded-lg"
              >
                Show Less
              </Button>
            </motion.div>
          )}

          {/* Trust Indicator */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-10 text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-sm border border-slate-200">
              <div className="flex -space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="text-gold fill-gold" size={16} />
                ))}
              </div>
              <span className="text-slate-700 font-medium ml-2">5.0 Average Rating</span>
              <span className="text-slate-400">|</span>
              <span className="text-slate-600">100+ Happy Clients</span>
            </div>
          </motion.div>

          {/* Leave a Review Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-6 text-center"
          >
            <Button
              onClick={() => setShowReviewModal(true)}
              className="bg-navy hover:bg-navy-light text-white font-semibold px-6 py-3 rounded-lg inline-flex items-center gap-2"
              data-testid="leave-review-btn"
            >
              <Plus size={18} />
              Leave a Review
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Booking Modal with Calendar */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-navy flex items-center gap-2">
              <CalendarCheck className="text-gold" size={24} />
              Schedule Your Appointment
            </DialogTitle>
            <DialogDescription>
              Select a date and time for your tax consultation
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Calendar */}
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                disabled={isDateDisabled}
                className="rounded-md border"
              />
            </div>

            {/* Time Slots */}
            {selectedDate && (
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">
                  Available times for {format(selectedDate, "MMMM d, yyyy")}:
                </p>
                {loadingTimes ? (
                  <p className="text-slate-500 text-sm">Loading available times...</p>
                ) : availableTimes.length === 0 ? (
                  <p className="text-amber-600 text-sm">No available times for this date. Please select another date.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {availableTimes.map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => setSelectedTime(time)}
                        className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                          selectedTime === time
                            ? "bg-navy text-white border-navy"
                            : "bg-white text-slate-700 border-slate-200 hover:border-navy"
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Contact Info */}
            {selectedTime && (
              <div className="space-y-3 pt-2 border-t">
                <div>
                  <label className="text-sm font-medium text-slate-700">Your Name *</label>
                  <Input
                    value={bookingForm.name}
                    onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                    placeholder="Full Name"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Email *</label>
                  <Input
                    type="email"
                    value={bookingForm.email}
                    onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                    placeholder="your@email.com"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Phone (Optional)</label>
                  <Input
                    value={bookingForm.phone}
                    onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            {/* Book Button */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSelectedDate(null);
                  setSelectedTime(null);
                  setBookingForm({ name: "", email: "", phone: "" });
                }}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handleBookAppointment}
                disabled={!selectedDate || !selectedTime || !bookingForm.name || !bookingForm.email || isBooking}
                className="flex-1 bg-navy hover:bg-navy-light text-white"
              >
                {isBooking ? "Booking..." : "Confirm Appointment"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Submission Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-navy flex items-center gap-2">
              <Star className="text-gold" size={24} />
              Share Your Experience
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitReview} className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Your Name *</label>
              <Input
                value={reviewForm.name}
                onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                placeholder="John D."
                required
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Location</label>
              <Input
                value={reviewForm.location}
                onChange={(e) => setReviewForm({ ...reviewForm, location: e.target.value })}
                placeholder="Memphis, TN"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Rating *</label>
              <div className="flex gap-1 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                    className="focus:outline-none"
                  >
                    <Star
                      size={28}
                      className={star <= reviewForm.rating ? "text-gold fill-gold" : "text-slate-300"}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Service Type</label>
              <select
                value={reviewForm.service}
                onChange={(e) => setReviewForm({ ...reviewForm, service: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy"
              >
                <option value="Tax Preparation">Tax Preparation</option>
                <option value="Business Tax">Business Tax</option>
                <option value="Self-Employment">Self-Employment</option>
                <option value="Credit Education">Credit Education</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Your Review *</label>
              <Textarea
                value={reviewForm.review}
                onChange={(e) => setReviewForm({ ...reviewForm, review: e.target.value })}
                placeholder="Share your experience with WMJ Enterprise Services..."
                required
                rows={4}
                className="mt-1"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowReviewModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submittingReview || !reviewForm.name || !reviewForm.review}
                className="flex-1 bg-navy hover:bg-navy-light text-white"
              >
                {submittingReview ? "Submitting..." : "Submit Review"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Referral Section */}
      <section className="py-10 bg-gold/10 border-y border-gold/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gold rounded-full mb-6">
              <span className="text-3xl">💰</span>
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-navy mb-4">
              Earn $50 for Every Referral
            </h2>
            <p className="text-slate-700 text-lg mb-6 max-w-2xl mx-auto">
              Refer a friend who files their taxes with WMJ Enterprise Services.
              Once they receive their refund, <strong>you receive $50</strong> as our thank-you.
            </p>
            <p className="text-slate-500 text-sm italic">
              Terms apply. Referral must complete filing and receive their refund.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 md:py-16 bg-[#F4F1E9]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-navy mb-6">
              Ready to File?
            </h2>
            <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
              Start your tax filing today. Secure, professional, and hassle-free.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/intake" data-testid="final-cta">
                <Button className="bg-gold text-navy hover:bg-gold-hover font-bold px-10 py-4 rounded-md text-lg transition-all duration-300 hover:shadow-glow">
                  Start Your Tax Filing Today
                  <ChevronRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="tel:+19014013054">
                <Button variant="outline" className="border-2 border-navy text-navy hover:bg-navy hover:text-white font-semibold px-8 py-4 text-lg transition-all duration-200">
                  <Phone className="mr-2" size={20} />
                  Call Now
                </Button>
              </a>
            </div>
            
            {/* Trust Signal */}
            <p className="mt-6 text-slate-500 text-sm flex items-center justify-center gap-2">
              <Lock size={14} />
              Your information is encrypted and protected
            </p>
          </motion.div>
        </div>
      </section>

      {/* Compliance Disclaimer */}
      <section className="py-8 bg-slate-100 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-xs text-slate-500 space-y-2">
            <p>
              <strong>Tax Preparer Disclaimer:</strong> WMJ Enterprise Services LLC provides tax preparation services. 
              We are not attorneys or CPAs unless otherwise specified. Tax advice provided is based on current IRS guidelines.
            </p>
            <p>
              <strong>Refund Timing:</strong> Refund timing depends on IRS processing. Most e-filed returns are processed within 21 days, 
              but delays may occur due to IRS review or errors.
            </p>
            <p>
              By submitting your information, you consent to our <Link to="/privacy" className="text-navy underline">Privacy Policy</Link> and 
              <Link to="/terms" className="text-navy underline ml-1">Terms of Service</Link>.
            </p>
          </div>
        </div>
      </section>

      <Footer />
      </main>
    </div>
  );
};

export default LandingPage;
